import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "../i18n";
import Sidebar from "../components/Sidebar.jsx";
import MediaSelector from "../components/MediaSelector.jsx";
import TagManager from "../components/TagManager.jsx";
import { notifyGenerationComplete } from "../services/notifications";
import { fetchBillingStatus, fetchCreditEstimate } from "../services/billing.js";
import "./NanoStudio.css";

import { buildDownloadName, downloadFile } from "../utils/download";

function escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stripMarkers(prompt, markers) {
  let output = prompt || "";
  markers.forEach((marker) => {
    if (!marker) return;
    const pattern = new RegExp(`\\s*${escapeRegex(marker)}\\s*`, "g");
    output = output.replace(pattern, " ").trim();
  });
  return output.replace(/\s{2,}/g, " ").trim();
}

export default function NanoBanana() {
  const storedState = useMemo(() => {
    try {
      const raw = window.sessionStorage.getItem("contentos_nano_state");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState("");
  const [models, setModels] = useState([]);
  const [ratios, setRatios] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [guide, setGuide] = useState({ groups: [], tags: [], presets: [] });

  const [modelId, setModelId] = useState(storedState.modelId || "");
  const [ratio, setRatio] = useState(storedState.ratio || "");
  const [size, setSize] = useState(storedState.size || "");
  const [multiTurn, setMultiTurn] = useState(Boolean(storedState.multiTurn));
  const [grain, setGrain] = useState(Number.isFinite(storedState.grain) ? storedState.grain : 0);
  const [contrast, setContrast] = useState(Number.isFinite(storedState.contrast) ? storedState.contrast : 65);
  const [promptText, setPromptText] = useState(storedState.promptText || "");
  const [inputMediaVersionIds, setInputMediaVersionIds] = useState(storedState.inputMediaVersionIds || []);
  const [selectedMediaEntries, setSelectedMediaEntries] = useState(storedState.selectedMediaEntries || []);
  const [baseMediaItemId, setBaseMediaItemId] = useState(storedState.baseMediaItemId || "");
  const [baseMediaVersionId, setBaseMediaVersionId] = useState(storedState.baseMediaVersionId || "");
  const [expertMode, setExpertMode] = useState(storedState.expertMode || "beginner");
  const [narrative, setNarrative] = useState(
    storedState.narrative || {
      subject: "",
      action: "",
      environment: "",
      temporal_progression: ""
    }
  );
  const [cinematography, setCinematography] = useState(
    storedState.cinematography || {
      shot_type: "",
      camera_motion: "",
      lens: "",
      focus: "",
      framing: ""
    }
  );
  const [visualStyle, setVisualStyle] = useState(
    storedState.visualStyle || {
      lighting: "",
      color_palette: "",
      mood: "",
      art_direction: ""
    }
  );
  const [constraints, setConstraints] = useState(
    storedState.constraints || {
      identity_consistency: true,
      no_style_drift: true,
      no_character_merge: true
    }
  );

  const [selectedTags, setSelectedTags] = useState(new Set(storedState.selectedTags || []));
  const [selectedMedia, setSelectedMedia] = useState(storedState.selectedMedia || []);

  const [promptRecommended, setPromptRecommended] = useState(storedState.promptRecommended || "");
  const [validationJson, setValidationJson] = useState(storedState.validationJson || null);
  const [validating, setValidating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [resultUrl, setResultUrl] = useState("");
  const [thought, setThought] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [lastResultUrl, setLastResultUrl] = useState(storedState.lastResultUrl || "");
  const [activeProjectId, setActiveProjectId] = useState("");
  const [recentImages, setRecentImages] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [zoomOverlays, setZoomOverlays] = useState([]);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const previewWrapRef = useRef(null);
  const previewImgRef = useRef(null);
  const cropDragRef = useRef(null);
  const [projectTags, setProjectTags] = useState([]);
  const [favoriteTags, setFavoriteTags] = useState([]);
  const [projectTagsLoading, setProjectTagsLoading] = useState(false);
  const [favoriteTagsLoading, setFavoriteTagsLoading] = useState(false);
  const appliedProjectRef = useRef("");
  const [userPresets, setUserPresets] = useState([]);
  const [userPresetsLoading, setUserPresetsLoading] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [showPresetForm, setShowPresetForm] = useState(false);
  const [selectedPresetIds, setSelectedPresetIds] = useState(new Set());
  const [billingStatus, setBillingStatus] = useState(null);
  const [creditEstimate, setCreditEstimate] = useState(1);
  const [billingError, setBillingError] = useState("");
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewRotation, setPreviewRotation] = useState(0);
  const [previewContrast, setPreviewContrast] = useState(100);
  const [cropMode, setCropMode] = useState(false);
  const [cropApplied, setCropApplied] = useState(false);
  const [cropRect, setCropRect] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [previewMeta, setPreviewMeta] = useState({
    naturalW: 0,
    naturalH: 0,
    displayW: 0,
    displayH: 0,
    offsetX: 0,
    offsetY: 0
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [editMediaItemId, setEditMediaItemId] = useState("");
  const [editMediaVersionId, setEditMediaVersionId] = useState("");
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareValue, setCompareValue] = useState(50);

  const [drawMode, setDrawMode] = useState(false);
  const [drawColor, setDrawColor] = useState("#FF0000");
  const [drawBrushSize, setDrawBrushSize] = useState(4);
  const [drawCursorPos, setDrawCursorPos] = useState({ x: 0, y: 0, visible: false });
  const drawCanvasRef = useRef(null);
  const drawBufferRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastDrawPointRef = useRef(null);

  useEffect(() => {
    if (!lastResultUrl) return;
    if (multiTurn) {
      setSelectedMediaEntries((prev) => {
        const rest = (prev || []).filter((entry) => entry.url !== lastResultUrl);
        return [{ url: lastResultUrl }, ...rest];
      });
      setSelectedMedia((prev) => {
        const rest = (prev || []).filter((url) => url !== lastResultUrl);
        return [lastResultUrl, ...rest];
      });
    } else {
      setSelectedMediaEntries((prev) => (prev || []).filter((entry) => entry.url !== lastResultUrl));
      setSelectedMedia((prev) => (prev || []).filter((url) => url !== lastResultUrl));
    }
  }, [lastResultUrl, multiTurn]);

  useEffect(() => {
    let isMounted = true;
    fetchBillingStatus()
      .then((data) => {
        if (!isMounted) return;
        setBillingStatus(data);
      })
      .catch((err) => {
        if (!isMounted) return;
        setBillingError(err.message || "Erreur billing.");
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const resolution = modelId.includes("gemini-3") ? (size || null) : (ratio || null);
    fetchCreditEstimate({
      gen_type: "image",
      model_id: modelId,
      resolution
    })
      .then((data) => {
        if (!isMounted) return;
        setCreditEstimate(Number(data.credits || 1));
      })
      .catch(() => {
        if (!isMounted) return;
        setCreditEstimate(1);
      });
    return () => {
      isMounted = false;
    };
  }, [modelId, ratio, size]);

  const handleNanoSelectionChange = (entries) => {
    const safeEntries = Array.isArray(entries) ? entries : [];
    setSelectedMediaEntries(safeEntries);
    setSelectedMedia(safeEntries.map((item) => item.url).filter(Boolean));
    setInputMediaVersionIds(safeEntries.map((item) => item.mediaVersionId).filter(Boolean));
    if (safeEntries[0]?.mediaItemId) {
      setBaseMediaItemId(safeEntries[0].mediaItemId || "");
      setBaseMediaVersionId(safeEntries[0].mediaVersionId || "");
    } else {
      setBaseMediaItemId("");
      setBaseMediaVersionId("");
    }
  };

  const updatePreviewMeta = () => {
    const wrap = previewWrapRef.current;
    const img = previewImgRef.current;
    if (!wrap || !img) return;
    const wrapRect = wrap.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    const offsetX = imgRect.left - wrapRect.left;
    const offsetY = imgRect.top - wrapRect.top;
    setPreviewMeta({
      naturalW: img.naturalWidth || 0,
      naturalH: img.naturalHeight || 0,
      displayW: imgRect.width || 0,
      displayH: imgRect.height || 0,
      offsetX,
      offsetY
    });
  };

  const clampCropRect = (nextRect) => {
    const minSize = 40;
    const maxW = previewMeta.displayW || 0;
    const maxH = previewMeta.displayH || 0;
    let { x, y, w, h } = nextRect;
    w = Math.max(minSize, Math.min(w, maxW));
    h = Math.max(minSize, Math.min(h, maxH));
    x = Math.max(0, Math.min(x, maxW - w));
    y = Math.max(0, Math.min(y, maxH - h));
    return { x, y, w, h };
  };

  const markerByTag = useMemo(() => {
    const map = new Map();
    guide.tags.forEach((tag) => map.set(tag.id, tag.marker));
    return map;
  }, [guide.tags]);

  const tagLabelById = useMemo(() => {
    const map = new Map();
    guide.tags.forEach((tag) => {
      const label = tag.label?.fr || tag.label?.en || tag.label || tag.id;
      map.set(tag.id, label);
    });
    return map;
  }, [guide.tags]);

  useEffect(() => {
    function handleMove(event) {
      if (resizeRef.current) {
        const { id, startX, startY, startWidth, startHeight, startPosX, startPosY, corner } = resizeRef.current;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        setZoomOverlays((prev) =>
          prev.map((item) => {
            if (item.id !== id) return item;
            const minWidth = 160;
            const minHeight = 120;
            let nextWidth = startWidth;
            let nextHeight = startHeight;
            let nextX = startPosX;
            let nextY = startPosY;
            if (corner.includes("e")) nextWidth = startWidth + deltaX;
            if (corner.includes("s")) nextHeight = startHeight + deltaY;
            if (corner.includes("w")) {
              nextWidth = startWidth - deltaX;
              nextX = startPosX + deltaX;
            }
            if (corner.includes("n")) {
              nextHeight = startHeight - deltaY;
              nextY = startPosY + deltaY;
            }
            if (nextWidth < minWidth) {
              if (corner.includes("w")) {
                nextX = startPosX + (startWidth - minWidth);
              }
              nextWidth = minWidth;
            }
            if (nextHeight < minHeight) {
              if (corner.includes("n")) {
                nextY = startPosY + (startHeight - minHeight);
              }
              nextHeight = minHeight;
            }
            return { ...item, width: nextWidth, height: nextHeight, x: nextX, y: nextY };
          })
        );
        return;
      }
      if (dragRef.current) {
        const { id, offsetX, offsetY } = dragRef.current;
        const nextX = event.clientX - offsetX;
        const nextY = event.clientY - offsetY;
        setZoomOverlays((prev) =>
          prev.map((item) => (item.id === id ? { ...item, x: nextX, y: nextY } : item))
        );
      }
    }
    function handleUp() {
      dragRef.current = null;
      resizeRef.current = null;
    }
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, []);

  function openZoom(url) {
    if (!url) return;
    const id = `zoom_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const width = 320;
    const height = 240;
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 640;
    const baseX = isMobile ? Math.max(20, (window.innerWidth - width) / 2) : 80;
    const baseY = isMobile ? Math.max(20, (window.innerHeight - height) / 2) : 80;
    setZoomOverlays((prev) => [
      ...prev,
      {
        id,
        url,
        x: baseX + (isMobile ? 0 : prev.length * 20),
        y: baseY + (isMobile ? 0 : prev.length * 20),
        width,
        height
      }
    ]);
  }

  function closeZoom(id) {
    setZoomOverlays((prev) => prev.filter((item) => item.id !== id));
  }

  function startDrag(event, overlay) {
    if (event.button !== 0) return;
    if (event.target.closest(".nano-zoom-resize")) return;
    dragRef.current = {
      id: overlay.id,
      offsetX: event.clientX - overlay.x,
      offsetY: event.clientY - overlay.y
    };
  }

  function startResize(event, overlay, corner) {
    event.stopPropagation();
    if (event.button !== 0) return;
    resizeRef.current = {
      id: overlay.id,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: overlay.width || 320,
      startHeight: overlay.height || 240,
      startPosX: overlay.x || 0,
      startPosY: overlay.y || 0,
      corner
    };
  }

  async function saveProjectTags(nextTags) {
    if (!activeProjectId) return;
    try {
      const res = await fetch("/api/fast/projects/tags", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: activeProjectId, tag_ids: nextTags })
      });
      if (!res.ok) throw new Error("Impossible d'enregistrer les tags du projet.");
      const data = await res.json();
      setProjectTags(Array.isArray(data.tag_ids) ? data.tag_ids : nextTags);
    } catch (err) {
      setErrorMsg(err.message || "Erreur tags projet.");
    }
  }

  async function saveFavoriteTags(nextTags) {
    try {
      const res = await fetch("/api/fast/user/tag-favorites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tag_ids: nextTags })
      });
      if (!res.ok) throw new Error("Impossible d'enregistrer les tags favoris.");
      const data = await res.json();
      setFavoriteTags(Array.isArray(data.tag_ids) ? data.tag_ids : nextTags);
    } catch (err) {
      setErrorMsg(err.message || "Erreur tags favoris.");
    }
  }

  function addSelectionToProject() {
    if (!activeProjectId) return;
    const next = Array.from(new Set([...projectTags, ...Array.from(selectedTags)]));
    saveProjectTags(next);
  }

  function removeProjectTag(tagId) {
    const next = projectTags.filter((tag) => tag !== tagId);
    saveProjectTags(next);
  }

  function clearProjectTags() {
    saveProjectTags([]);
  }

  function addSelectionToFavorites() {
    const next = Array.from(new Set([...favoriteTags, ...Array.from(selectedTags)]));
    saveFavoriteTags(next);
  }

  function removeFavoriteTag(tagId) {
    const next = favoriteTags.filter((tag) => tag !== tagId);
    saveFavoriteTags(next);
  }

  function clearFavoriteTags() {
    saveFavoriteTags([]);
  }

  useEffect(() => {
    let isMounted = true;
    async function loadConfig() {
      setLoadingConfig(true);
      setConfigError("");
      try {
        const res = await fetch("/api/fast/nano/config?locale=fr");
        if (!res.ok) throw new Error("Impossible de charger la configuration.");
        const data = await res.json();
        if (!isMounted) return;
        setModels(data.models || []);
        setRatios(data.ratios || []);
        setSizes(data.sizes || []);
        setGuide(data.guide || { groups: [], tags: [], presets: [] });
        if (!modelId && data.models?.length) setModelId(data.models[0].id);
        if (!ratio && data.ratios?.length) setRatio(data.ratios[0].value);
        if (!size && data.sizes?.length) setSize(data.sizes[0].value);
      } catch (err) {
        if (isMounted) setConfigError(err.message || "Erreur inconnue.");
      } finally {
        if (isMounted) setLoadingConfig(false);
      }
    }
    loadConfig();
    return () => {
      isMounted = false;
    };
  }, [modelId, ratio, size]);

  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem("contentos_active_project_id");
      if (stored) setActiveProjectId(stored);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadActiveProject() {
      try {
        const res = await fetch("/api/fast/user/settings");
        if (!res.ok) return;
        const data = await res.json();
        if (isMounted) setActiveProjectId(data.active_project_id || "");
      } catch {
        // ignore
      }
    }
    loadActiveProject();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadRecentImages() {
      setRecentLoading(true);
      try {
        const params = new URLSearchParams();
        if (activeProjectId) params.set("project_id", activeProjectId);
        const query = params.toString();
        const res = await fetch(`/api/fast/media/recent/images${query ? `?${query}` : ""}`);
        if (!res.ok) throw new Error("Impossible de charger les images recentes.");
        const data = await res.json();
        if (isMounted) setRecentImages(Array.isArray(data.items) ? data.items : []);
      } catch {
        if (isMounted) setRecentImages([]);
      } finally {
        if (isMounted) setRecentLoading(false);
      }
    }
    loadRecentImages();
    return () => {
      isMounted = false;
    };
  }, [activeProjectId, lastResultUrl]);

  useEffect(() => {
    const currentPreview = resultUrl || lastResultUrl || "";
    if (!currentPreview) return () => {};
    const handleResize = () => updatePreviewMeta();
    const timer = window.setTimeout(updatePreviewMeta, 0);
    window.addEventListener("resize", handleResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [resultUrl, lastResultUrl]);

  useEffect(() => {
    const currentPreview = resultUrl || lastResultUrl || "";
    if (!currentPreview) return;
    setPreviewRotation(0);
    setPreviewContrast(100);
    setCropMode(false);
    setCropApplied(false);
    setCropRect({ x: 0, y: 0, w: 0, h: 0 });
  }, [resultUrl, lastResultUrl]);

  useEffect(() => {
    if (!cropMode || cropRect.w || cropRect.h) return;
    if (!previewMeta.displayW || !previewMeta.displayH) return;
    const w = Math.max(previewMeta.displayW * 0.7, 120);
    const h = Math.max(previewMeta.displayH * 0.7, 120);
    setCropRect(
      clampCropRect({
        x: Math.max((previewMeta.displayW - w) / 2, 0),
        y: Math.max((previewMeta.displayH - h) / 2, 0),
        w,
        h
      })
    );
  }, [cropMode, cropRect.w, cropRect.h, previewMeta.displayW, previewMeta.displayH]);

  useEffect(() => {
    const currentPreview = resultUrl || lastResultUrl || "";
    if (editMediaItemId || !currentPreview) return;
    const match = recentImages.find((item) => item?.asset?.url === currentPreview);
    if (match?.generation?.media_item_id && match?.generation?.output_media_version_id) {
      setEditMediaItemId(match.generation.media_item_id);
      setEditMediaVersionId(match.generation.output_media_version_id);
    }
  }, [editMediaItemId, resultUrl, lastResultUrl, recentImages]);

  useEffect(() => {
    let isMounted = true;
    async function loadFavoriteTags() {
      setFavoriteTagsLoading(true);
      try {
        const res = await fetch("/api/fast/user/tag-favorites");
        if (!res.ok) throw new Error("Impossible de charger les tags favoris.");
        const data = await res.json();
        if (isMounted) setFavoriteTags(Array.isArray(data.tag_ids) ? data.tag_ids : []);
      } catch {
        if (isMounted) setFavoriteTags([]);
      } finally {
        if (isMounted) setFavoriteTagsLoading(false);
      }
    }
    loadFavoriteTags();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadProjectTags() {
      if (!activeProjectId) {
        if (isMounted) setProjectTags([]);
        return;
      }
      setProjectTagsLoading(true);
      try {
        const res = await fetch(`/api/fast/projects/tags?project_id=${encodeURIComponent(activeProjectId)}`);
        if (!res.ok) throw new Error("Impossible de charger les tags projet.");
        const data = await res.json();
        if (isMounted) setProjectTags(Array.isArray(data.tag_ids) ? data.tag_ids : []);
      } catch {
        if (isMounted) setProjectTags([]);
      } finally {
        if (isMounted) setProjectTagsLoading(false);
      }
    }
    loadProjectTags();
    return () => {
      isMounted = false;
    };
  }, [activeProjectId]);

  useEffect(() => {
    let isMounted = true;
    async function loadUserPresets() {
      setUserPresetsLoading(true);
      try {
        const res = await fetch("/api/fast/nano/presets?target=nano");
        if (!res.ok) throw new Error("Impossible de charger les presets.");
        const data = await res.json();
        if (isMounted) setUserPresets(Array.isArray(data.presets) ? data.presets : []);
      } catch {
        if (isMounted) setUserPresets([]);
      } finally {
        if (isMounted) setUserPresetsLoading(false);
      }
    }
    loadUserPresets();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!activeProjectId) return;
    if (appliedProjectRef.current === activeProjectId) return;
    const markerList = guide.tags.map((tag) => tag.marker);
    const projectMarkers = projectTags
      .map((tagId) => markerByTag.get(tagId))
      .filter(Boolean);
    setSelectedTags(new Set(projectTags));
    setPromptText((current) => {
      const basePrompt = stripMarkers(current, markerList);
      return [basePrompt, ...projectMarkers].filter(Boolean).join(" ").trim();
    });
    appliedProjectRef.current = activeProjectId;
  }, [projectTags, activeProjectId, guide.tags, markerByTag]);

  useEffect(() => {
    async function consumePickerSelection() {
      const params = new URLSearchParams(window.location.search);
      if (params.get("media_picker_done") !== "1") return;
      if ((params.get("target") || "") !== "nano") return;
      const clearSelection = async () => {
        try {
          await fetch("/api/fast/media/selection", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ image_paths: [], target: "nano" })
          });
        } catch {
          // Ignore errors clearing selection.
        }
      };
      let handled = false;
      try {
        const raw = window.sessionStorage.getItem("contentos_media_picker_payload");
        if (raw) {
          const payload = JSON.parse(raw);
          if (payload?.target === "nano" && Array.isArray(payload.images)) {
            const images = payload.images;
            const versions = Array.isArray(payload.versions) ? payload.versions : [];
            const entries = versions.length
              ? versions.map((item) => ({
                url: item.url,
                thumbnail_url: item.thumbnailUrl || "",
                mediaItemId: item.mediaItemId || "",
                mediaVersionId: item.mediaVersionId || "",
                assetId: item.assetId || ""
              }))
              : images.map((url) => ({ url }));
            setSelectedMediaEntries(entries);
            setSelectedMedia(entries.map((item) => item.url).filter(Boolean));
            const versionIds = entries.map((item) => item.mediaVersionId).filter(Boolean);
            if (versionIds.length) setInputMediaVersionIds(versionIds);
            if (versions[0]?.mediaItemId) {
              setBaseMediaItemId(versions[0].mediaItemId || "");
              setBaseMediaVersionId(versions[0].mediaVersionId || "");
            }
            window.sessionStorage.removeItem("contentos_media_picker_payload");
            handled = true;
          }
        }
      } catch {
        // Ignore storage errors.
      }
      try {
        if (!handled) {
          const res = await fetch("/api/fast/media/selection");
          if (!res.ok) return;
          const data = await res.json();
          const selection = data.selection || {};
          const images = Array.isArray(selection.images) ? selection.images : [];
          if (images.length) {
            const entries = images.map((url) => ({ url }));
            setSelectedMediaEntries(entries);
            setSelectedMedia(images);
            setInputMediaVersionIds([]);
          }
        }
      } finally {
        await clearSelection();
        params.delete("media_picker_done");
        params.delete("target");
        const base = window.location.pathname;
        const query = params.toString();
        window.history.replaceState({}, "", query ? `${base}?${query}` : base);
      }
    }
    consumePickerSelection();
  }, []);

  useEffect(() => {
    const payload = {
      modelId,
      ratio,
      size,
      multiTurn,
      grain,
      contrast,
      promptText,
      inputMediaVersionIds,
      baseMediaItemId,
      baseMediaVersionId,
      expertMode,
      narrative,
      cinematography,
      visualStyle,
      constraints,
      selectedTags: Array.from(selectedTags),
      selectedMedia,
      selectedMediaEntries,
      lastResultUrl,
      promptRecommended,
      validationJson
    };
    try {
      window.sessionStorage.setItem("contentos_nano_state", JSON.stringify(payload));
    } catch {
      // Ignore storage errors.
    }
  }, [
    lastResultUrl,
    modelId,
    multiTurn,
    promptText,
    ratio,
    selectedMedia,
    selectedMediaEntries,
    selectedTags,
    size,
    expertMode,
    narrative,
    cinematography,
    visualStyle,
    constraints,
    promptRecommended,
    validationJson,
    grain,
    contrast,
    inputMediaVersionIds,
    baseMediaItemId,
    baseMediaVersionId
  ]);

  function toggleTag(tagId) {
    const marker = markerByTag.get(tagId);
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
        if (marker && expertMode === "beginner") {
          setPromptText((current) => stripMarkers(current, [marker]));
        }
      } else {
        next.add(tagId);
        if (marker && expertMode === "beginner") {
          setPromptText((current) => {
            if (current.includes(marker)) return current;
            return `${current} ${marker}`.trim();
          });
        }
      }
      return next;
    });
  }

  function applyPreset(preset) {
    const markerList = guide.tags.map((tag) => tag.marker);
    const presetMarkers = preset.tag_ids
      .map((tagId) => markerByTag.get(tagId))
      .filter(Boolean);
    setSelectedTags(new Set(preset.tag_ids));
    if (expertMode === "beginner") {
      const basePrompt = stripMarkers(promptText, markerList);
      setPromptText([basePrompt, ...presetMarkers].filter(Boolean).join(" ").trim());
    }
  }

  function applyUserPreset(preset) {
    if (preset.tag_ids?.length) setSelectedTags(new Set(preset.tag_ids));
    if (preset.prompt_text) setPromptText(preset.prompt_text);
    if (preset.model_id) setModelId(preset.model_id);
    if (preset.aspect_ratio) setRatio(preset.aspect_ratio);
    if (preset.image_size) setSize(preset.image_size);
    if (typeof preset.grain === "number") setGrain(preset.grain);
    if (typeof preset.contrast === "number") setContrast(preset.contrast);
  }

  async function saveUserPreset() {
    if (!presetName.trim()) {
      setErrorMsg("Veuillez entrer un nom pour le preset.");
      return;
    }
    try {
      const res = await fetch("/api/fast/nano/presets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: presetName.trim(),
          target: "nano",
          tag_ids: Array.from(selectedTags),
          prompt_text: promptText,
          model_id: modelId,
          grain,
          contrast,
          aspect_ratio: ratio,
          image_size: size
        })
      });
      if (!res.ok) throw new Error("Impossible de sauvegarder le preset.");
      const data = await res.json();
      if (data.preset) {
        setUserPresets((prev) => [...prev, data.preset]);
      }
      setPresetName("");
      setShowPresetForm(false);
    } catch (err) {
      setErrorMsg(err.message || "Erreur sauvegarde preset.");
    }
  }

  async function deleteSelectedPresets() {
    if (selectedPresetIds.size === 0) return;
    try {
      const res = await fetch("/api/fast/nano/presets", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ preset_ids: Array.from(selectedPresetIds) })
      });
      if (!res.ok) throw new Error("Impossible de supprimer les presets.");
      setUserPresets((prev) => prev.filter((p) => !selectedPresetIds.has(p.preset_id)));
      setSelectedPresetIds(new Set());
    } catch (err) {
      setErrorMsg(err.message || "Erreur suppression preset.");
    }
  }

  function togglePresetSelection(presetId) {
    setSelectedPresetIds((prev) => {
      const next = new Set(prev);
      if (next.has(presetId)) {
        next.delete(presetId);
      } else {
        next.add(presetId);
      }
      return next;
    });
  }

  function buildPromptFromText(text) {
    const markerList = Array.from(selectedTags)
      .map((tagId) => markerByTag.get(tagId))
      .filter(Boolean);
    let output = stripMarkers(text || "", markerList).trim();
    if (markerList.length) output = `${output} ${markerList.join(" ")}`.trim();
    const tweaks = [];
    if (grain !== 50) tweaks.push(`grain ${grain}`);
    if (contrast !== 50) tweaks.push(`contrast ${contrast}`);
    if (tweaks.length) output = `${output} ${tweaks.join(", ")}`.trim();
    return output;
  }

  function buildExpertPrompt() {
    const narrativeParts = [
      narrative.subject,
      narrative.action,
      narrative.environment,
      narrative.temporal_progression
    ]
      .map((item) => item.trim())
      .filter(Boolean);
    const cameraParts = [
      cinematography.shot_type,
      cinematography.camera_motion,
      cinematography.lens,
      cinematography.focus,
      cinematography.framing
    ]
      .map((item) => item.trim())
      .filter(Boolean);
    const styleParts = [
      visualStyle.lighting,
      visualStyle.color_palette,
      visualStyle.mood,
      visualStyle.art_direction
    ]
      .map((item) => item.trim())
      .filter(Boolean);
    const constraintsParts = [];
    if (constraints.identity_consistency) constraintsParts.push("identity consistency");
    if (constraints.no_style_drift) constraintsParts.push("no style drift");
    if (constraints.no_character_merge) constraintsParts.push("no character merge");
    return [
      narrativeParts.join(" "),
      cameraParts.length ? `Camera: ${cameraParts.join(", ")}` : "",
      styleParts.length ? `Style: ${styleParts.join(", ")}` : "",
      constraintsParts.length ? `Constraints: ${constraintsParts.join(", ")}` : ""
    ]
      .filter(Boolean)
      .join("\n");
  }

  function buildFinalPrompt() {
    const basePrompt = expertMode === "expert" ? buildExpertPrompt() : promptText.trim();
    return buildPromptFromText(basePrompt);
  }

  function buildPromptData() {
    return {
      narrative: { ...narrative },
      cinematography: { ...cinematography },
      visual_style: { ...visualStyle },
      constraints: { ...constraints },
      fine_tuning: {
        grain,
        contrast
      }
    };
  }

  function applyPromptData(data, fallbackPrompt) {
    const promptData = data && typeof data === "object" ? data : {};
    const narrativeData = promptData.narrative || {};
    const cineData = promptData.cinematography || {};
    const styleData = promptData.visual_style || {};
    const constraintData = promptData.constraints || {};
    setNarrative({
      subject: narrativeData.subject || "",
      action: narrativeData.action || fallbackPrompt || "",
      environment: narrativeData.environment || "",
      temporal_progression: narrativeData.temporal_progression || ""
    });
    setCinematography({
      shot_type: cineData.shot_type || "",
      camera_motion: cineData.camera_motion || "",
      lens: cineData.lens || "",
      focus: cineData.focus || "",
      framing: cineData.framing || ""
    });
    setVisualStyle({
      lighting: styleData.lighting || "",
      color_palette: styleData.color_palette || "",
      mood: styleData.mood || "",
      art_direction: styleData.art_direction || ""
    });
    setConstraints({
      identity_consistency: constraintData.identity_consistency ?? true,
      no_style_drift: constraintData.no_style_drift ?? true,
      no_character_merge: constraintData.no_character_merge ?? true
    });
  }

  async function handleValidate() {
    const effectivePrompt = buildFinalPrompt().trim();
    if (!effectivePrompt) return;
    setValidating(true);
    setErrorMsg("");
    setPromptRecommended("");
    setValidationJson(null);
    try {
      const uniqueInputs = [...new Set(selectedMedia)];
      if (multiTurn && lastResultUrl) uniqueInputs.unshift(lastResultUrl);
      const res = await fetch("/api/fast/nano/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: effectivePrompt,
          grain,
          contrast,
          input_urls: uniqueInputs,
          input_media_version_ids: inputMediaVersionIds,
          base_media_item_id: baseMediaItemId || null,
          base_media_version_id: baseMediaVersionId || null
        })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || "Validation impossible.");
      setPromptRecommended(data.prompt_recommended || "");
      setValidationJson(data);
      applyPromptData(data.prompt_data, effectivePrompt);
    } catch (err) {
      setErrorMsg(err.message || "Erreur validation.");
    } finally {
      setValidating(false);
    }
  }

  async function handleGenerate() {
    const finalPrompt = buildFinalPrompt();
    if (!finalPrompt.trim()) return;
    setGenerating(true);
    setErrorMsg("");
    setThought("");
    try {
      const storedProjectId = (() => {
        try {
          return window.sessionStorage.getItem("contentos_active_project_id") || "";
        } catch {
          return "";
        }
      })();
      const projectId = activeProjectId || storedProjectId || null;
      const uniqueInputs = [...new Set(selectedMedia)];
      if (multiTurn && lastResultUrl) uniqueInputs.unshift(lastResultUrl);
      let effectivePrompt = finalPrompt;
      if (hasDrawContent()) {
        try {
          const sketchUrl = await uploadDrawing();
          if (sketchUrl) {
            uniqueInputs.push(sketchUrl);
            effectivePrompt = `${finalPrompt} Croquis de reference joint.`;
          }
        } catch {
          // drawing upload failed, continue without it
        }
      }
      const promptInitial = promptText.trim() || (expertMode === "expert" ? buildExpertPrompt() : finalPrompt);
      const payload = {
        prompt: effectivePrompt,
        model_id: modelId,
        aspect_ratio: ratio || null,
        image_size: modelId.includes("gemini-3") ? size || null : null,
        input_urls: uniqueInputs,
        input_media_version_ids: inputMediaVersionIds,
        base_media_item_id: baseMediaItemId || null,
        base_media_version_id: baseMediaVersionId || null,
        grain,
        contrast,
        project_id: projectId,
        prompt_initial: promptInitial,
        prompt_final: effectivePrompt,
        prompt_recommended: promptRecommended || null,
        prompt_data: buildPromptData(),
        tags: Array.from(selectedTags)
      };
      const jobRes = await fetch("/api/fast/jobs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ gen_type: "image", payload })
      });
      if (!jobRes.ok) {
        const err = await jobRes.json().catch(() => ({}));
        if (jobRes.status === 429 && err.detail?.code === "TOO_MANY_CONCURRENT_JOBS") {
          setErrorMsg(`Limite atteinte : ${err.detail.active_jobs}/${err.detail.limit} jobs en cours. Attendez qu'un job se termine.`);
          return;
        }
        throw new Error(err.detail || "Soumission impossible.");
      }
      const { job_id: jobId } = await jobRes.json();
      let data = null;
      for (let attempt = 0; attempt < 120; attempt++) {
        await new Promise(r => setTimeout(r, 2000));
        const poll = await (await fetch(`/api/fast/jobs/${jobId}`)).json();
        if (poll.status === "done") { data = poll.result; break; }
        if (poll.status === "error") throw new Error(poll.error || "Génération échouée.");
      }
      if (!data) throw new Error("Délai d'attente dépassé.");
      if (!data.ok) throw new Error(data.error || "Generation impossible.");
      setResultUrl(data.url || "");
      setLastResultUrl(data.url || "");
      setThought(data.thought || "");
      setEditMediaItemId(data.media_item_id || "");
      setEditMediaVersionId(data.media_version_id || "");
      // Notification de succes
      notifyGenerationComplete("image", true, {
        message: "Votre image a ete generee avec succes !",
        url: "/nano"
      });
    } catch (err) {
      setErrorMsg(err.message || "Erreur generation.");
      // Notification d'echec
      notifyGenerationComplete("image", false, {
        error: err.message || "Erreur lors de la generation"
      });
    } finally {
      setGenerating(false);
    }
  }

  const isGemini3 = modelId.includes("gemini-3");
  const creditsRemaining = billingStatus?.credits_remaining;
  const creditsNeeded = Number.isFinite(creditEstimate) ? creditEstimate : 1;
  const hasCredits = typeof creditsRemaining === "number" ? creditsRemaining >= creditsNeeded : true;
  const missingCredits =
    typeof creditsRemaining === "number" ? Math.max(creditsNeeded - creditsRemaining, 0) : 0;
  const previewUrl = resultUrl || lastResultUrl || "";
  const compareUrl = resultUrl && lastResultUrl ? lastResultUrl : "";
  const cropInset =
    cropApplied && previewMeta.displayW && previewMeta.displayH
      ? `inset(${(cropRect.y / previewMeta.displayH) * 100}% ${((previewMeta.displayW - cropRect.x - cropRect.w) / previewMeta.displayW) * 100}% ${((previewMeta.displayH - cropRect.y - cropRect.h) / previewMeta.displayH) * 100}% ${(cropRect.x / previewMeta.displayW) * 100}%)`
      : "none";
  const previewStyle = {
    transform: `scale(${previewZoom}) rotate(${previewRotation}deg)`,
    filter: `contrast(${previewContrast}%)`,
    clipPath: cropInset
  };

  const handleFitPreview = () => {
    setPreviewZoom(1);
    setPreviewRotation(0);
    setPreviewContrast(100);
    setCropMode(false);
    setCropApplied(false);
    setCropRect({ x: 0, y: 0, w: 0, h: 0 });
  };

  const hasEditChanges =
    previewRotation % 360 !== 0 ||
    previewContrast !== 100 ||
    Boolean(cropRect.w && cropRect.h);

  const handleSaveEdits = async () => {
    if (!editMediaItemId || !editMediaVersionId) {
      setEditError("Aucun media editable.");
      return;
    }
    const hasCrop = cropRect.w && cropRect.h;
    const hasRotate = previewRotation % 360 !== 0;
    const hasContrast = previewContrast !== 100;
    if (!hasCrop && !hasRotate && !hasContrast) {
      setEditError("Aucune modification a appliquer.");
      return;
    }
    setEditSaving(true);
    setEditError("");
    const scaleX = previewMeta.displayW ? previewMeta.naturalW / previewMeta.displayW : 1;
    const scaleY = previewMeta.displayH ? previewMeta.naturalH / previewMeta.displayH : 1;
    const payload = {
      media_item_id: editMediaItemId,
      media_version_id: editMediaVersionId,
      rotate_deg: previewRotation,
      contrast: previewContrast
    };
    if (hasCrop) {
      payload.crop_x = Math.round(cropRect.x * scaleX);
      payload.crop_y = Math.round(cropRect.y * scaleY);
      payload.crop_width = Math.round(cropRect.w * scaleX);
      payload.crop_height = Math.round(cropRect.h * scaleY);
    }
    try {
      const res = await fetch("/api/fast/media/transform", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Erreur transformation media.");
      }
      setResultUrl(data.url || "");
      setLastResultUrl(data.url || "");
      setEditMediaVersionId(data.media_version_id || editMediaVersionId);
      setPreviewRotation(0);
      setPreviewContrast(100);
      setCropApplied(false);
      setCropMode(false);
      setCropRect({ x: 0, y: 0, w: 0, h: 0 });
    } catch (err) {
      setEditError(err.message || "Erreur transformation media.");
    } finally {
      setEditSaving(false);
    }
  };

  /* ── Drawing layer helpers ── */

  const ensureDrawBuffer = useCallback(() => {
    const img = previewImgRef.current;
    const targetW = (img && img.naturalWidth) ? img.naturalWidth : 1024;
    const targetH = (img && img.naturalHeight) ? img.naturalHeight : 1024;
    const buf = drawBufferRef.current;
    if (buf && buf.width === targetW && buf.height === targetH) return buf;
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    if (buf) {
      const ctx = canvas.getContext("2d");
      ctx.drawImage(buf, 0, 0);
    }
    drawBufferRef.current = canvas;
    return canvas;
  }, []);

  const renderDrawLayer = useCallback(() => {
    const canvas = drawCanvasRef.current;
    const wrap = previewWrapRef.current;
    const buf = drawBufferRef.current;
    if (!canvas || !wrap || !buf) return;
    const img = previewImgRef.current;
    const wrapRect = wrap.getBoundingClientRect();
    let dw, dh, ox, oy;
    if (img && img.naturalWidth) {
      const imgRect = img.getBoundingClientRect();
      dw = imgRect.width;
      dh = imgRect.height;
      ox = imgRect.left - wrapRect.left;
      oy = imgRect.top - wrapRect.top;
    } else {
      dw = wrapRect.width;
      dh = wrapRect.height;
      ox = 0;
      oy = 0;
    }
    canvas.style.left = `${ox}px`;
    canvas.style.top = `${oy}px`;
    canvas.style.width = `${dw}px`;
    canvas.style.height = `${dh}px`;
    const dpr = window.devicePixelRatio || 1;
    const pw = Math.floor(dw * dpr);
    const ph = Math.floor(dh * dpr);
    if (canvas.width !== pw || canvas.height !== ph) {
      canvas.width = pw;
      canvas.height = ph;
    }
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, pw, ph);
    ctx.setTransform(pw / buf.width, 0, 0, ph / buf.height, 0, 0);
    ctx.drawImage(buf, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }, []);

  const drawAtPoint = useCallback(
    (point, isStart) => {
      const buf = ensureDrawBuffer();
      if (!buf || !point) return;
      const ctx = buf.getContext("2d");
      const canvas = drawCanvasRef.current;
      if (!canvas || !canvas.clientWidth || !canvas.clientHeight) return;
      const scaleX = buf.width / canvas.clientWidth;
      const scaleY = buf.height / canvas.clientHeight;
      const bx = point.x * scaleX;
      const by = point.y * scaleY;
      const size = Math.max(1, drawBrushSize * scaleX);
      ctx.save();
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = size;
      ctx.strokeStyle = drawColor;
      ctx.fillStyle = drawColor;
      ctx.globalAlpha = 1;
      if (isStart || !lastDrawPointRef.current) {
        ctx.beginPath();
        ctx.arc(bx, by, size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(lastDrawPointRef.current.x * scaleX, lastDrawPointRef.current.y * scaleY);
        ctx.lineTo(bx, by);
        ctx.stroke();
      }
      ctx.restore();
      renderDrawLayer();
    },
    [drawBrushSize, drawColor, ensureDrawBuffer, renderDrawLayer]
  );

  const getDrawPointerPos = useCallback((event) => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }, []);

  const clearDrawing = useCallback(() => {
    const buf = drawBufferRef.current;
    if (buf) {
      const ctx = buf.getContext("2d");
      ctx.clearRect(0, 0, buf.width, buf.height);
    }
    renderDrawLayer();
  }, [renderDrawLayer]);

  const hasDrawContent = useCallback(() => {
    const buf = drawBufferRef.current;
    if (!buf) return false;
    const ctx = buf.getContext("2d");
    const data = ctx.getImageData(0, 0, buf.width, buf.height).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) return true;
    }
    return false;
  }, []);

  const uploadDrawing = useCallback(async () => {
    const buf = drawBufferRef.current;
    if (!buf) return "";
    const blob = await new Promise((resolve) => buf.toBlob(resolve, "image/png"));
    if (!blob) return "";
    const formData = new FormData();
    formData.append("files", blob, `sketch_${Date.now()}.png`);
    const res = await fetch("/api/fast/media/uploads", { method: "POST", body: formData });
    if (!res.ok) return "";
    const data = await res.json().catch(() => ({}));
    const items = Array.isArray(data.items) ? data.items : [];
    return items[0]?.url || "";
  }, []);

  useEffect(() => {
    if (!drawMode) return;
    ensureDrawBuffer();
    const timer = requestAnimationFrame(() => renderDrawLayer());
    return () => cancelAnimationFrame(timer);
  }, [drawMode, previewUrl, ensureDrawBuffer, renderDrawLayer]);

  useEffect(() => {
    if (!drawMode) return;
    const handleResize = () => renderDrawLayer();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [drawMode, renderDrawLayer]);

  return (
    <div className="app-shell">
      <Sidebar active="nano" />
      <div className="app-main">
        <div className="nano-page">
          <header className="nano-header">
            <div>
              <p className="nano-kicker">{t("nano.kicker")}</p>
              <h1 className="nano-title page-title-accent">{t("nano.title")}</h1>
              <p className="nano-subtitle">{t("nano.subtitle")}</p>
            </div>
            <div className="nano-header-actions">
              <button className="nano-pill" type="button" onClick={() => (window.location.href = "/projects")}>
                <span className="material-symbols-rounded">arrow_back</span>
                Retour projets
              </button>
              <button className="nano-pill accent" type="button">
                Credits: {typeof creditsRemaining === "number" ? creditsRemaining : "--"}
              </button>
            </div>
          </header>

          {configError && <div className="nano-alert error">{configError}</div>}
          {errorMsg && <div className="nano-alert error">{errorMsg}</div>}
          {editError && <div className="nano-alert error">{editError}</div>}

          <div className="nano-layout">
            <section className="nano-canvas-area">
              <div className="nano-canvas-toolbar">
                <div className="nano-toolgroup">
                  <button
                    className="nano-tool"
                    type="button"
                    onClick={() => setPreviewZoom((prev) => Math.max(0.6, prev - 0.1))}
                    disabled={!previewUrl || previewZoom <= 0.6}
                  >
                    <span className="material-symbols-rounded">zoom_out</span>
                  </button>
                  <button
                    className="nano-tool"
                    type="button"
                    onClick={() => setPreviewZoom((prev) => Math.min(2.5, prev + 0.1))}
                    disabled={!previewUrl || previewZoom >= 2.5}
                  >
                    <span className="material-symbols-rounded">zoom_in</span>
                  </button>
                  <button
                    className="nano-tool"
                    type="button"
                    onClick={handleFitPreview}
                    disabled={!previewUrl}
                  >
                    <span className="material-symbols-rounded">fit_screen</span>
                    Fit
                  </button>
                </div>
                <div className="nano-toolgroup">
                  <button
                    className="nano-tool"
                    type="button"
                    onClick={() => setPreviewRotation((prev) => (prev - 90 + 360) % 360)}
                    disabled={!previewUrl}
                  >
                    <span className="material-symbols-rounded">rotate_left</span>
                  </button>
                  <button
                    className="nano-tool"
                    type="button"
                    onClick={() => setPreviewRotation((prev) => (prev + 90) % 360)}
                    disabled={!previewUrl}
                  >
                    <span className="material-symbols-rounded">rotate_right</span>
                  </button>
                  <button
                    className={`nano-tool ${cropMode ? "active" : ""}`}
                    type="button"
                    onClick={() => {
                      if (cropMode) {
                        setCropMode(false);
                        setCropApplied(Boolean(cropRect.w && cropRect.h));
                        return;
                      }
                      setCropMode(true);
                      setCropApplied(false);
                    }}
                    disabled={!previewUrl}
                  >
                    <span className="material-symbols-rounded">crop</span>
                    Recadrage
                  </button>
                  <button
                    className="nano-tool"
                    type="button"
                    onClick={handleSaveEdits}
                    disabled={!editMediaItemId || !editMediaVersionId || editSaving || !hasEditChanges}
                  >
                    <span className="material-symbols-rounded">done</span>
                    {editSaving ? "Sauvegarde..." : "Valider"}
                  </button>
                </div>
                <div className="nano-toolgroup">
                  <label className="nano-contrast">
                    Contraste
                    <input
                      type="range"
                      min="60"
                      max="140"
                      value={previewContrast}
                      onChange={(e) => setPreviewContrast(Number(e.target.value))}
                      disabled={!previewUrl}
                    />
                  </label>
                </div>
                <div className="nano-toolgroup nano-draw-toolgroup">
                  <button
                    className={`nano-tool${drawMode ? " active" : ""}`}
                    type="button"
                    onClick={() => setDrawMode((prev) => !prev)}
                  >
                    <span className="material-symbols-rounded">draw</span>
                    Dessin
                  </button>
                  {drawMode && (
                    <>
                      <div className="nano-draw-swatches">
                        {["#FF0000", "#3B82F6", "#22C55E", "#FACC15", "#FFFFFF", "#000000"].map((c) => (
                          <button
                            key={c}
                            type="button"
                            className={`nano-draw-swatch${drawColor === c ? " active" : ""}`}
                            style={{ background: c }}
                            onClick={() => setDrawColor(c)}
                            aria-label={c}
                          />
                        ))}
                        <input
                          type="color"
                          className="nano-draw-color-input"
                          value={drawColor}
                          onChange={(e) => setDrawColor(e.target.value)}
                          title="Couleur personnalisee"
                        />
                      </div>
                      <label className="nano-draw-size-label">
                        <span>{drawBrushSize}px</span>
                        <input
                          type="range"
                          min="1"
                          max="100"
                          value={drawBrushSize}
                          onChange={(e) => setDrawBrushSize(Number(e.target.value))}
                        />
                      </label>
                      <button className="nano-tool" type="button" onClick={clearDrawing}>
                        <span className="material-symbols-rounded">delete</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="nano-canvas-stage">
                {(previewUrl || drawMode) ? (
                  <div className={`nano-preview${!previewUrl && drawMode ? " nano-preview-blank" : ""}`} ref={previewWrapRef}>
                    {previewUrl && (
                      <img
                        ref={previewImgRef}
                        src={previewUrl}
                        alt="Rendu"
                        style={previewStyle}
                        onLoad={updatePreviewMeta}
                      />
                    )}
                    {compareEnabled && compareUrl && (
                      <>
                        <div className="nano-compare">
                          <span>Avant</span>
                          <div className="nano-compare-track">
                            <div className="nano-compare-handle" style={{ left: `${compareValue}%` }} />
                          </div>
                          <span>Apres</span>
                        </div>
                        <img
                          className="nano-preview-compare"
                          src={compareUrl}
                          alt="Comparaison"
                          style={{
                            ...previewStyle,
                            clipPath: `inset(0 0 0 ${compareValue}%)`
                          }}
                        />
                        <input
                          className="nano-compare-range"
                          type="range"
                          min="0"
                          max="100"
                          value={compareValue}
                          onChange={(e) => setCompareValue(Number(e.target.value))}
                        />
                      </>
                    )}
                    {cropMode && previewMeta.displayW > 0 && previewMeta.displayH > 0 && (
                      <div
                        className="media-edit-crop"
                        style={{
                          left: previewMeta.offsetX + cropRect.x,
                          top: previewMeta.offsetY + cropRect.y,
                          width: cropRect.w,
                          height: cropRect.h
                        }}
                        onPointerDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          cropDragRef.current = {
                            handle: "move",
                            startX: event.clientX,
                            startY: event.clientY,
                            rect: { ...cropRect }
                          };
                          const onMove = (moveEvent) => {
                            const payload = cropDragRef.current;
                            if (!payload) return;
                            const dx = moveEvent.clientX - payload.startX;
                            const dy = moveEvent.clientY - payload.startY;
                            setCropRect(
                              clampCropRect({
                                ...payload.rect,
                                x: payload.rect.x + dx,
                                y: payload.rect.y + dy
                              })
                            );
                          };
                          const onUp = () => {
                            cropDragRef.current = null;
                            window.removeEventListener("pointermove", onMove);
                            window.removeEventListener("pointerup", onUp);
                          };
                          window.addEventListener("pointermove", onMove);
                          window.addEventListener("pointerup", onUp);
                        }}
                      >
                        {["nw", "ne", "sw", "se"].map((handle) => (
                          <button
                            key={handle}
                            type="button"
                            className={`media-edit-handle ${handle}`}
                            onPointerDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              cropDragRef.current = {
                                handle,
                                startX: event.clientX,
                                startY: event.clientY,
                                rect: { ...cropRect }
                              };
                              const onMove = (moveEvent) => {
                                const payload = cropDragRef.current;
                                if (!payload) return;
                                const dx = moveEvent.clientX - payload.startX;
                                const dy = moveEvent.clientY - payload.startY;
                                const minSize = 40;
                                let { x, y, w, h } = payload.rect;
                                if (payload.handle.includes("n")) {
                                  y = y + dy;
                                  h = payload.rect.h - dy;
                                }
                                if (payload.handle.includes("s")) {
                                  h = payload.rect.h + dy;
                                }
                                if (payload.handle.includes("w")) {
                                  x = x + dx;
                                  w = payload.rect.w - dx;
                                }
                                if (payload.handle.includes("e")) {
                                  w = payload.rect.w + dx;
                                }
                                if (w < minSize || h < minSize) return;
                                setCropRect(clampCropRect({ x, y, w, h }));
                              };
                              const onUp = () => {
                                cropDragRef.current = null;
                                window.removeEventListener("pointermove", onMove);
                                window.removeEventListener("pointerup", onUp);
                              };
                              window.addEventListener("pointermove", onMove);
                              window.addEventListener("pointerup", onUp);
                            }}
                            aria-label="Redimensionner le cadre"
                          />
                        ))}
                      </div>
                    )}
                    {drawMode && (
                      <>
                        <canvas
                          ref={drawCanvasRef}
                          className="nano-draw-canvas"
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.currentTarget.setPointerCapture(e.pointerId);
                            isDrawingRef.current = true;
                            const pt = getDrawPointerPos(e);
                            setDrawCursorPos({ x: e.clientX, y: e.clientY, visible: true });
                            lastDrawPointRef.current = pt;
                            drawAtPoint(pt, true);
                          }}
                          onPointerMove={(e) => {
                            setDrawCursorPos({ x: e.clientX, y: e.clientY, visible: true });
                            if (!isDrawingRef.current) return;
                            const pt = getDrawPointerPos(e);
                            drawAtPoint(pt, false);
                            lastDrawPointRef.current = pt;
                          }}
                          onPointerUp={(e) => {
                            e.currentTarget.releasePointerCapture(e.pointerId);
                            isDrawingRef.current = false;
                            lastDrawPointRef.current = null;
                          }}
                          onPointerLeave={() => {
                            isDrawingRef.current = false;
                            lastDrawPointRef.current = null;
                            setDrawCursorPos((prev) => ({ ...prev, visible: false }));
                          }}
                        />
                        <div
                          className="nano-draw-cursor"
                          style={{
                            width: drawBrushSize,
                            height: drawBrushSize,
                            left: drawCursorPos.x - (previewWrapRef.current?.getBoundingClientRect().left || 0),
                            top: drawCursorPos.y - (previewWrapRef.current?.getBoundingClientRect().top || 0),
                            opacity: drawCursorPos.visible ? 1 : 0
                          }}
                        />
                      </>
                    )}
                    {resultUrl && (
                      <button
                        type="button"
                        className="nano-download-link"
                        onClick={() => downloadFile(resultUrl, buildDownloadName(resultUrl))}
                      >
                        <span className="material-symbols-rounded">download</span>
                        Telecharger
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="nano-preview-empty">Aucun rendu pour le moment.</div>
                )}
              </div>
            </section>

            <aside className="nano-controls">
              <section className="nano-panel">
                <div className="nano-panel-header">
                  <h2>Photos de references</h2>
                </div>
                <MediaSelector
                  title={t("nano.images.title")}
                  selected={selectedMediaEntries}
                  onChange={handleNanoSelectionChange}
                  maxCount={14}
                  allowUpload
                  allowLibrary
                  pickerTarget="nano"
                  pickerReturn="/nano"
                  pickerMulti
                />
              </section>

              <section className="nano-panel">
                <div className="nano-panel-header">
                  <h2>{t("nano.config.title")}</h2>
                </div>
                {loadingConfig ? (
                  <p>{t("common.loading")}</p>
                ) : (
                  <div className="nano-config-row">
                    <label>
                      {t("nano.config.model")}
                      <select value={modelId} onChange={(e) => setModelId(e.target.value)}>
                        {models.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {t("nano.config.format")}
                      <select value={ratio} onChange={(e) => setRatio(e.target.value)}>
                        {ratios.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {isGemini3 && (
                      <label>
                        {t("nano.config.resolution")}
                        <select value={size} onChange={(e) => setSize(e.target.value)}>
                          {sizes.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <label className="nano-range">
                      {t("nano.config.grain")} <span>{grain}</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={grain}
                        onChange={(e) => setGrain(Number(e.target.value))}
                      />
                    </label>
                    <label className="nano-range">
                      {t("nano.config.contrast")} <span>{contrast}</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={contrast}
                        onChange={(e) => setContrast(Number(e.target.value))}
                      />
                    </label>
                    <label className="nano-toggle">
                      <input type="checkbox" checked={multiTurn} onChange={(e) => setMultiTurn(e.target.checked)} />
                      {t("nano.config.multiturn")}
                    </label>
                  </div>
                )}
              </section>

              <section className="nano-panel">
                <div className="nano-panel-header">
                  <h2>{t("nano.prompt.title")}</h2>
                </div>
                <div className="nano-mode-toggle">
                  <span>{t("nano.mode.label")}</span>
                  <button
                    className={expertMode === "beginner" ? "nano-mode-button active" : "nano-mode-button"}
                    onClick={() => setExpertMode("beginner")}
                  >
                    {t("nano.mode.beginner")}
                  </button>
                  <button
                    className={expertMode === "expert" ? "nano-mode-button active" : "nano-mode-button"}
                    onClick={() => setExpertMode("expert")}
                  >
                    {t("nano.mode.expert")}
                  </button>
                </div>
                {expertMode === "beginner" ? (
                  <textarea
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                    rows={6}
                    placeholder={t("nano.prompt.placeholder")}
                  />
                ) : (
                  <div className="nano-expert-blocks">
                    <div className="nano-expert-card">
                      <h3>{t("nano.expert.narrative.title")}</h3>
                      <div className="nano-expert-grid">
                        <input
                          type="text"
                          placeholder={t("nano.expert.narrative.subject")}
                          value={narrative.subject}
                          onChange={(e) => setNarrative((prev) => ({ ...prev, subject: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("nano.expert.narrative.action")}
                          value={narrative.action}
                          onChange={(e) => setNarrative((prev) => ({ ...prev, action: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("nano.expert.narrative.environment")}
                          value={narrative.environment}
                          onChange={(e) => setNarrative((prev) => ({ ...prev, environment: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("nano.expert.narrative.temporal")}
                          value={narrative.temporal_progression}
                          onChange={(e) =>
                            setNarrative((prev) => ({ ...prev, temporal_progression: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <div className="nano-expert-card">
                      <h3>{t("nano.expert.camera.title")}</h3>
                      <div className="nano-expert-grid">
                        <input
                          type="text"
                          placeholder={t("nano.expert.camera.shotType")}
                          value={cinematography.shot_type}
                          onChange={(e) => setCinematography((prev) => ({ ...prev, shot_type: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("nano.expert.camera.cameraMotion")}
                          value={cinematography.camera_motion}
                          onChange={(e) =>
                            setCinematography((prev) => ({ ...prev, camera_motion: e.target.value }))
                          }
                        />
                        <input
                          type="text"
                          placeholder={t("nano.expert.camera.lens")}
                          value={cinematography.lens}
                          onChange={(e) => setCinematography((prev) => ({ ...prev, lens: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("nano.expert.camera.focus")}
                          value={cinematography.focus}
                          onChange={(e) => setCinematography((prev) => ({ ...prev, focus: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("nano.expert.camera.framing")}
                          value={cinematography.framing}
                          onChange={(e) => setCinematography((prev) => ({ ...prev, framing: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="nano-expert-card">
                      <h3>{t("nano.expert.style.title")}</h3>
                      <div className="nano-expert-grid">
                        <input
                          type="text"
                          placeholder={t("nano.expert.style.lighting")}
                          value={visualStyle.lighting}
                          onChange={(e) => setVisualStyle((prev) => ({ ...prev, lighting: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("nano.expert.style.colorPalette")}
                          value={visualStyle.color_palette}
                          onChange={(e) => setVisualStyle((prev) => ({ ...prev, color_palette: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("nano.expert.style.mood")}
                          value={visualStyle.mood}
                          onChange={(e) => setVisualStyle((prev) => ({ ...prev, mood: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("nano.expert.style.artDirection")}
                          value={visualStyle.art_direction}
                          onChange={(e) => setVisualStyle((prev) => ({ ...prev, art_direction: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="nano-expert-card">
                      <h3>{t("nano.expert.constraints.title")}</h3>
                      <div className="nano-expert-constraints">
                        <label>
                          <input
                            type="checkbox"
                            checked={constraints.identity_consistency}
                            onChange={(e) =>
                              setConstraints((prev) => ({ ...prev, identity_consistency: e.target.checked }))
                            }
                          />
                          {t("nano.expert.constraints.identity")}
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={constraints.no_style_drift}
                            onChange={(e) =>
                              setConstraints((prev) => ({ ...prev, no_style_drift: e.target.checked }))
                            }
                          />
                          {t("nano.expert.constraints.noStyleDrift")}
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={constraints.no_character_merge}
                            onChange={(e) =>
                              setConstraints((prev) => ({ ...prev, no_character_merge: e.target.checked }))
                            }
                          />
                          {t("nano.expert.constraints.noCharacterMerge")}
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </section>

              <section className="nano-panel">
                <div className="nano-panel-header">
                  <h2>Tags & presets</h2>
                </div>
                <div className="nano-presets">
                  <div className="nano-presets-header">
                    <h3>{t("nano.prompt.presets")}</h3>
                    <div className="nano-presets-actions">
                      {selectedPresetIds.size > 0 && (
                        <button
                          type="button"
                          className="nano-secondary-button nano-delete-presets"
                          onClick={deleteSelectedPresets}
                        >
                          Supprimer ({selectedPresetIds.size})
                        </button>
                      )}
                      <button
                        type="button"
                        className="nano-secondary-button"
                        onClick={() => setShowPresetForm(!showPresetForm)}
                      >
                        {showPresetForm ? "Annuler" : "+ Nouveau"}
                      </button>
                    </div>
                  </div>
                  {showPresetForm && (
                    <div className="nano-preset-form">
                      <input
                        type="text"
                        placeholder="Nom du preset"
                        value={presetName}
                        onChange={(e) => setPresetName(e.target.value)}
                      />
                      <button type="button" className="nano-primary" onClick={saveUserPreset}>
                        Enregistrer
                      </button>
                    </div>
                  )}
                  <div className="nano-chip-row">
                    {guide.presets.map((preset) => (
                      <button key={preset.id} className="nano-chip preset" onClick={() => applyPreset(preset)}>
                        {preset.label}
                      </button>
                    ))}
                    {userPresets.map((preset) => (
                      <span key={preset.preset_id} className="nano-user-preset-chip">
                        <input
                          type="checkbox"
                          checked={selectedPresetIds.has(preset.preset_id)}
                          onChange={() => togglePresetSelection(preset.preset_id)}
                        />
                        <button
                          type="button"
                          className="nano-chip preset user"
                          onClick={() => applyUserPreset(preset)}
                        >
                          {preset.name}
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
                <TagManager
                  prefix="nano"
                  tagsContainerClass="nano-tags"
                  headingTag="h3"
                  guide={guide}
                  selectedTags={selectedTags}
                  tagLabelById={tagLabelById}
                  projectTags={projectTags}
                  favoriteTags={favoriteTags}
                  activeProjectId={activeProjectId}
                  projectTagsLoading={projectTagsLoading}
                  favoriteTagsLoading={favoriteTagsLoading}
                  onAddSelectionToProject={addSelectionToProject}
                  onAddSelectionToFavorites={addSelectionToFavorites}
                  onClearProjectTags={clearProjectTags}
                  onClearFavoriteTags={clearFavoriteTags}
                  onRemoveProjectTag={removeProjectTag}
                  onRemoveFavoriteTag={removeFavoriteTag}
                  onToggleTag={toggleTag}
                />
              </section>

              <section className="nano-panel">
                <div className="nano-panel-header">
                  <h2>Generation</h2>
                </div>
                <div className="nano-prompt-actions">
                  <button
                    type="button"
                    className="nano-secondary-button"
                    disabled={!promptRecommended}
                    onClick={() => setPromptText(promptRecommended || "")}
                  >
                    Copier le prompt recommande
                  </button>
                  <div className="nano-prompt-actions-right">
                    <button
                      className="nano-secondary-button"
                      disabled={generating || validating || !buildFinalPrompt().trim()}
                      onClick={handleValidate}
                    >
                      {validating ? t("nano.validating") : t("nano.validate")}
                    </button>
                    <button
                      className="nano-primary"
                      disabled={generating || validating || !buildFinalPrompt().trim() || !hasCredits}
                      onClick={handleGenerate}
                    >
                      {generating ? t("nano.generating") : t("nano.generate")}
                      <span className="credit-badge">{creditsNeeded}</span>
                    </button>
                  </div>
                </div>
                {billingError && <div className="nano-alert error">{billingError}</div>}
                {!hasCredits && missingCredits > 0 && (
                  <button
                    type="button"
                    className="billing-upgrade-link"
                    onClick={() => (window.location.href = "/pricing")}
                  >
                    {missingCredits} / {creditsNeeded} credits manquants rechargez !
                  </button>
                )}
              </section>

              <section className="nano-panel">
                <div className="nano-panel-header">
                  <h2>Recommandations</h2>
                </div>
                <div className="nano-recommended">
                  <h3>{t("nano.prompt.recommended")}</h3>
                  {promptRecommended ? (
                    <pre className="nano-recommended-block">{promptRecommended}</pre>
                  ) : (
                    <p className="nano-muted">{t("nano.prompt.recommended_empty")}</p>
                  )}
                  {validationJson && (
                    <>
                      <h3>JSON validation</h3>
                      <pre className="nano-json-block">{JSON.stringify(validationJson, null, 2)}</pre>
                    </>
                  )}
                </div>
              </section>
            </aside>
          </div>

          <section className="nano-panel nano-history">
            <div className="nano-panel-header">
              <h2>Historique</h2>
            </div>
            {recentLoading && <p>{t("common.loading")}</p>}
            {!recentLoading && recentImages.length === 0 && <p>Aucune image recente.</p>}
            {!recentLoading && recentImages.length > 0 && (
              <div className="nano-recent-grid">
                {recentImages.map((item, index) => {
                  const previewUrl = item?.asset?.thumbnail_url || item?.asset?.url || "";
                  return (
                    <div key={`${item?.generation?.generation_id || "img"}-${index}`} className="nano-recent-item">
                      {previewUrl ? (
                        <>
                          <img src={previewUrl} alt="recent" />
                          <button
                            type="button"
                            className="nano-zoom-button"
                            onClick={() => openZoom(item?.asset?.url || previewUrl)}
                            aria-label="Zoom"
                          >
                            <span className="material-symbols-rounded">zoom_in</span>
                          </button>
                        </>
                      ) : (
                        <div className="nano-recent-empty">Preview</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          <div className="nano-zoom-layer">
            {zoomOverlays.map((overlay) => (
              <div
                key={overlay.id}
                className="nano-zoom-window"
                style={{
                  left: overlay.x,
                  top: overlay.y,
                  width: overlay.width,
                  height: overlay.height
                }}
                onMouseDown={(event) => startDrag(event, overlay)}
              >
                <button
                  type="button"
                  className="nano-zoom-close"
                  onClick={(event) => {
                    event.stopPropagation();
                    closeZoom(overlay.id);
                  }}
                  aria-label="Fermer"
                >
                  <span className="material-symbols-rounded">close</span>
                </button>
                <div className="nano-zoom-content">
                  <img src={overlay.url} alt="Zoom" />
                </div>
                <button
                  type="button"
                  className="nano-zoom-resize nano-zoom-resize-nw"
                  onMouseDown={(event) => startResize(event, overlay, "nw")}
                  aria-label="Redimensionner"
                />
                <button
                  type="button"
                  className="nano-zoom-resize nano-zoom-resize-ne"
                  onMouseDown={(event) => startResize(event, overlay, "ne")}
                  aria-label="Redimensionner"
                />
                <button
                  type="button"
                  className="nano-zoom-resize nano-zoom-resize-sw"
                  onMouseDown={(event) => startResize(event, overlay, "sw")}
                  aria-label="Redimensionner"
                />
                <button
                  type="button"
                  className="nano-zoom-resize nano-zoom-resize-se"
                  onMouseDown={(event) => startResize(event, overlay, "se")}
                  aria-label="Redimensionner"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
